<!--

@@author        j0Shi
@@date          2019.08.20
@@desc          Vacancy button template for the object page
@@usedby        ObjectVacancyPrice -> ObjectVavancyForm > Object

-->

<template>
  <div>
    <div
      v-if="showRecommendationInfo"
      class="d-none alert alert-info d-sm-flex"
    >
      <div>
        <i class="icon">
          <icon-info-circle-alternative />
        </i>
      </div>
      <div class="ml-2">
        {{ $t('booking:recommendation.' + recommendation) }}
      </div>
    </div>

    <template v-if="!showRecommendationInfo">
      <router-link-simple
        v-if="data.priceObject.price !== null && privateObject === 0"
        class="btn btn-primary btn-block mt-1"
        :class="{ disabled: isFetchingPrice }"
        :to="{
          name: 'Booking', params: {
            objectNumber: data.priceObject.objectNumber
          },
          query: {
            ...params
          }
        }"
        data-ident="toBooking"
        @click.native="togglePreventScroll(false)"
      >
        <i
          v-show="isFetchingPrice"
          class="icon spinning-icon"
        >
          <icon-spinner
            class="svg-icon svg-fill"
          />
        </i>
        {{ $t('object:vacancy.price.bookButton') }}
      </router-link-simple>
      <router-link-simple
        v-else-if="data.status === 'error' || fetchingPriceError || !hasVacancyData || privateObject === 1"
        class="btn btn-primary btn-block mt-1"
        :class="{ disabled: isFetchingPrice }"
        :to="{
          name: 'Request', params: {
            objectNumber: objectNumber
          },
          query: {
            ...params
          }
        }"
      >
        <i
          v-show="isFetchingPrice"
          class="icon spinning-icon"
        >
          <icon-spinner
            class="svg-icon svg-fill"
          />
        </i>
        {{ $t('object:vacancy.buttons.request') }}
      </router-link-simple>
      <button
        v-else
        class="btn btn-primary btn-block mt-1 flatpickr-ignore"
        type="button"
        :disabled="isFetchingPrice"
        data-ident="pickDateBtn"
        @click="$emit('click-pick-date', $event)"
      >
        <i
          v-show="isFetchingPrice"
          class="icon spinning-icon"
        >
          <icon-spinner
            class="svg-icon svg-fill"
          />
        </i>
        <template v-if="data.status === 'occupied'">
          {{ $t('object:vacancy.price.changeDateButton') }}
        </template>
        <template v-else>
          {{ $t('object:vacancy.price.pickDateButton') }}
        </template>
      </button>
    </template>

    <a
      v-if="data.priceObject.price !== null && privateObject === 0 && paxloungeUrl !== ''"
      class="d-none d-sm-block btn btn-dark btn-block mt-2"
      :class="{ disabled: isFetchingPrice }"
      :href="paxloungeUrl"
    >
      <i
        v-show="isFetchingPrice"
        class="icon spinning-icon"
      >
        <icon-spinner
          class="svg-icon svg-fill"
        />
      </i>
      {{ $t('object:vacancy.price.sendToPaxLounge') }}
    </a>
  </div>
</template>

<script>
import UserApi from 'mixins/UserApi';
import { Base64 } from 'js-base64';
import RouterLinkSimple from 'components/RouterLinkSimple.vue';
import ActiveObjectStore from 'mixins/ActiveObjectStore';
import IconInfoCircleAlternative from 'ICON:IconInfoCircleAlternative';
import {
  modalLogic,
} from 'mixins/composition/PickerModal';

import IconSpinner from 'ICON:IconSpinner';

export default {
  name: 'ObjectVacancyButtons',
  components: {
    IconSpinner,
    IconInfoCircleAlternative,
    RouterLinkSimple,
  },
  mixins: [
    // store helper with necessary getters to deal with active objects
    ActiveObjectStore, UserApi,
  ],
  i18nOptions: { namespaces: ['booking'] },
  props: {
    // data from price request
    data: {
      type: Object,
      required: true,
    },
    // query parameter from vacancy form, used for booking and request routes
    params: {
      type: Object,
      required: true,
    },
    // marker for existing vacancy data, used to conditionaly display info
    hasVacancyData: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const {
      togglePreventScroll,
    } = modalLogic();

    return {
      togglePreventScroll,
    };
  },
  computed: {
    privateObject() { return this.getObjectDataByKey('privateObject'); },
    isFetchingPrice() {
      return this.$store.getters.getIsFetchingPrice;
    },
    fetchingPriceError() {
      return this.$store.getters.getFetchingPriceError;
    },
    objectNumber() { return this.getObjectDataByKey('objectNumber'); },
    recommendation() {
      return this.data.recommendation || '';
    },
    hostName() {
      if (document.location && !document.location.host.match(/localhost/)) { return document.location.host; }
      return 'wlv.ferienhausangebote.com';
    },
    paxloungeUrl() {
      let url = '';
      if (
        this.UserApi_checkPermission('object.usePaxlounge')
          && this.$store.getters.getPartnerConfigValue('paxloungeNr') !== ''
          && this.params.startDate
          && this.params.endDate
          && this.params.persons
          && this.data?.priceObject?.price
          && this.data?.priceObject?.currency
          && this.data?.terms?.agency?.file
      ) {
        // url = `${process.env.API_PROXY_HOST}/object/pax/${this.objectNumber}?`;
        url = `https://${this.hostName}/api/object/pax/${this.objectNumber}?`;
        url = `${url}startDate=${this.params.startDate}`;
        url = `${url}&endDate=${this.params.endDate}`;
        url = `${url}&persons=${this.params.persons}`;
        url = `${url}&price=${this.data.priceObject.priceComplete}`;
        url = `${url}&currency=${this.data.priceObject.currency}`;
        url = `${url}&term=${encodeURI(this.data.terms.agency.file)}`;
        url = `${url}&mandant=${process.env.CLIENTNAME}`;
        url = `${url}&partner=${this.$store.getters.getPartnerConfigValue('partnerID')}`;
        url = `${url}&host=${this.hostName}`;

        url = `paxconnector://v1/offer/new/${Base64.encode(url)}`;
      }
      return url;
    },
    showRecommendationInfo() {
      if (this.recommendation && this.UserApi_checkPermission('pricerequest.showRecommendation')) {
        return true;
      }
      return false;
    },
  },

  created() {
    this.$store.dispatch('SAVE_FETCHING_PRICE_ERROR', { status: false });
  },
};
</script>

<style lang="scss" scoped>
  .disabled {
    opacity: 0.5;
    pointer-events: none;
  }
</style>
